import {
  FORMA_PAGO,
  REGIMEN_FISCAL,
  USO_CFDI,
  clienteCreateSchema,
  entradasOrdenadas,
  esPersonaMoral,
  validarRegimenContraRfc,
} from "@uirtus/shared";
import { useMemo, useState, type FormEvent } from "react";
import type { Cliente } from "../lib/clientes";
import { AreaTexto, Aviso, Boton, Campo, Entrada, Seleccion } from "./ui";

type Campos = {
  rfc: string;
  razonSocial: string;
  nombreComercial: string;
  regimenFiscal: string;
  codigoPostal: string;
  usoCfdiDefault: string;
  formaPagoDefault: string;
  email: string;
  telefono: string;
  sitioWeb: string;
  diasCredito: string;
  notas: string;
};

const vacio: Campos = {
  rfc: "",
  razonSocial: "",
  nombreComercial: "",
  regimenFiscal: "",
  codigoPostal: "",
  usoCfdiDefault: "G03",
  formaPagoDefault: "03",
  email: "",
  telefono: "",
  sitioWeb: "",
  diasCredito: "0",
  notas: "",
};

const desdeCliente = (c: Cliente): Campos => ({
  rfc: c.rfc,
  razonSocial: c.razonSocial,
  nombreComercial: c.nombreComercial ?? "",
  regimenFiscal: c.regimenFiscal,
  codigoPostal: c.codigoPostal,
  usoCfdiDefault: c.usoCfdiDefault ?? "",
  formaPagoDefault: c.formaPagoDefault ?? "",
  email: c.email ?? "",
  telefono: c.telefono ?? "",
  sitioWeb: c.sitioWeb ?? "",
  diasCredito: String(c.diasCredito),
  notas: c.notas ?? "",
});

/** Los opcionales vacíos se omiten; mandar "" haría fallar la validación. */
const limpiar = (v: string): string | undefined => (v.trim() ? v.trim() : undefined);

interface Props {
  cliente?: Cliente;
  enviando?: boolean;
  errorServidor?: string | null;
  alEnviar: (datos: Record<string, unknown>) => void;
  alCancelar: () => void;
}

export function FormularioCliente({
  cliente,
  enviando,
  errorServidor,
  alEnviar,
  alCancelar,
}: Props) {
  const [campos, setCampos] = useState<Campos>(
    cliente ? desdeCliente(cliente) : vacio,
  );
  const [errores, setErrores] = useState<Record<string, string>>({});

  const set = (llave: keyof Campos) => (valor: string) => {
    setCampos((previos) => ({ ...previos, [llave]: valor }));
    setErrores(({ [llave]: _, ...resto }) => resto);
  };

  /**
   * Aviso en vivo de incoherencia entre RFC y régimen, antes de enviar.
   * Es la misma función que corre en el backend, importada de shared: no hay
   * forma de que la pantalla y la API discrepen.
   */
  const avisoRegimen = useMemo(
    () =>
      campos.rfc && campos.regimenFiscal
        ? validarRegimenContraRfc(campos.rfc.toUpperCase(), campos.regimenFiscal)
        : null,
    [campos.rfc, campos.regimenFiscal],
  );

  const tipoPersona = useMemo(() => {
    const rfc = campos.rfc.trim();
    if (rfc.length === 12) return "Persona moral";
    if (rfc.length === 13) return "Persona física";
    return null;
  }, [campos.rfc]);

  function enviar(e: FormEvent) {
    e.preventDefault();

    const datos = {
      rfc: campos.rfc.trim().toUpperCase(),
      razonSocial: campos.razonSocial.trim(),
      nombreComercial: limpiar(campos.nombreComercial),
      regimenFiscal: campos.regimenFiscal,
      codigoPostal: campos.codigoPostal.trim(),
      usoCfdiDefault: limpiar(campos.usoCfdiDefault),
      formaPagoDefault: limpiar(campos.formaPagoDefault),
      email: limpiar(campos.email),
      telefono: limpiar(campos.telefono),
      sitioWeb: limpiar(campos.sitioWeb),
      diasCredito: Number(campos.diasCredito || 0),
      notas: limpiar(campos.notas),
    };

    // Se valida con el MISMO esquema del backend. Si pasa aquí, pasa allá.
    const resultado = clienteCreateSchema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErrores(porCampo);
      return;
    }

    alEnviar(datos);
  }

  return (
    <form onSubmit={enviar} className="space-y-5 p-5">
      {errorServidor && <Aviso>{errorServidor}</Aviso>}

      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Datos fiscales
        </legend>
        <p className="text-xs text-slate-500">
          Deben coincidir exactamente con la Constancia de Situación Fiscal. El
          SAT valida RFC, nombre y código postal contra su padrón, y cualquier
          diferencia rechaza el timbrado.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="RFC"
            requerido
            error={errores["rfc"]}
            ayuda={tipoPersona ?? "12 caracteres para moral, 13 para física"}
          >
            <Entrada
              value={campos.rfc}
              onChange={(e) => set("rfc")(e.target.value.toUpperCase())}
              error={!!errores["rfc"]}
              maxLength={13}
              className="font-mono uppercase"
              placeholder="XAXX010101000"
            />
          </Campo>

          <Campo etiqueta="Código postal" requerido error={errores["codigoPostal"]}>
            <Entrada
              value={campos.codigoPostal}
              onChange={(e) =>
                set("codigoPostal")(e.target.value.replace(/\D/g, "").slice(0, 5))
              }
              error={!!errores["codigoPostal"]}
              inputMode="numeric"
              className="font-mono"
              placeholder="64000"
            />
          </Campo>
        </div>

        <Campo
          etiqueta="Razón social"
          requerido
          error={errores["razonSocial"]}
          ayuda="Sin el régimen societario: no escribas 'SA de CV'"
        >
          <Entrada
            value={campos.razonSocial}
            onChange={(e) => set("razonSocial")(e.target.value)}
            error={!!errores["razonSocial"]}
          />
        </Campo>

        <Campo
          etiqueta="Régimen fiscal"
          requerido
          error={errores["regimenFiscal"] ?? avisoRegimen ?? undefined}
        >
          <Seleccion
            value={campos.regimenFiscal}
            onChange={(e) => set("regimenFiscal")(e.target.value)}
            error={!!errores["regimenFiscal"] || !!avisoRegimen}
          >
            <option value="">Selecciona…</option>
            {entradasOrdenadas(REGIMEN_FISCAL).map(([clave, nombre]) => (
              <option key={clave} value={clave}>
                {clave} · {nombre}
              </option>
            ))}
          </Seleccion>
        </Campo>

        {campos.rfc.length >= 12 && !avisoRegimen && campos.regimenFiscal && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400">
            El régimen es congruente con un RFC de{" "}
            {esPersonaMoral(campos.rfc) ? "persona moral" : "persona física"}.
          </p>
        )}

        <Campo etiqueta="Nombre comercial" error={errores["nombreComercial"]}>
          <Entrada
            value={campos.nombreComercial}
            onChange={(e) => set("nombreComercial")(e.target.value)}
          />
        </Campo>
      </fieldset>

      <fieldset className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Preferencias de facturación
        </legend>

        <div className="grid gap-4 sm:grid-cols-3">
          <Campo etiqueta="Uso de CFDI habitual" error={errores["usoCfdiDefault"]}>
            <Seleccion
              value={campos.usoCfdiDefault}
              onChange={(e) => set("usoCfdiDefault")(e.target.value)}
            >
              <option value="">Sin preferencia</option>
              {entradasOrdenadas(USO_CFDI).map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {clave} · {nombre}
                </option>
              ))}
            </Seleccion>
          </Campo>

          <Campo etiqueta="Forma de pago habitual" error={errores["formaPagoDefault"]}>
            <Seleccion
              value={campos.formaPagoDefault}
              onChange={(e) => set("formaPagoDefault")(e.target.value)}
            >
              <option value="">Sin preferencia</option>
              {entradasOrdenadas(FORMA_PAGO).map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {clave} · {nombre}
                </option>
              ))}
            </Seleccion>
          </Campo>

          <Campo etiqueta="Días de crédito" error={errores["diasCredito"]}>
            <Entrada
              type="number"
              min={0}
              max={365}
              value={campos.diasCredito}
              onChange={(e) => set("diasCredito")(e.target.value)}
              error={!!errores["diasCredito"]}
            />
          </Campo>
        </div>
      </fieldset>

      <fieldset className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Contacto
        </legend>

        <div className="grid gap-4 sm:grid-cols-3">
          <Campo etiqueta="Correo" error={errores["email"]}>
            <Entrada
              type="email"
              value={campos.email}
              onChange={(e) => set("email")(e.target.value)}
              error={!!errores["email"]}
            />
          </Campo>
          <Campo etiqueta="Teléfono" error={errores["telefono"]}>
            <Entrada
              value={campos.telefono}
              onChange={(e) => set("telefono")(e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Sitio web" error={errores["sitioWeb"]}>
            <Entrada
              value={campos.sitioWeb}
              onChange={(e) => set("sitioWeb")(e.target.value)}
            />
          </Campo>
        </div>

        <Campo etiqueta="Notas" error={errores["notas"]}>
          <AreaTexto
            rows={3}
            value={campos.notas}
            onChange={(e) => set("notas")(e.target.value)}
          />
        </Campo>
      </fieldset>

      <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
        <Boton type="button" variante="secundario" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" disabled={enviando}>
          {enviando ? "Guardando…" : cliente ? "Guardar cambios" : "Crear cliente"}
        </Boton>
      </div>
    </form>
  );
}
